import React from 'react';

// One place for how HTTP methods and status codes look, so the request list, the
// detail pane, the tunnel feed and the mock rules all agree.

const METHOD_TEXT = {
  GET: 'text-pp-get',
  POST: 'text-pp-post',
  PUT: 'text-pp-put',
  PATCH: 'text-pp-patch',
  DELETE: 'text-pp-delete',
};

const METHOD_TINT = {
  GET: 'bg-pp-get/[0.14] text-pp-get',
  POST: 'bg-pp-post/[0.14] text-pp-post',
  PUT: 'bg-pp-put/[0.14] text-pp-put',
  PATCH: 'bg-pp-patch/[0.14] text-pp-patch',
  DELETE: 'bg-pp-delete/[0.12] text-pp-delete',
};

export function methodTextClass(method) {
  return METHOD_TEXT[String(method || '').toUpperCase()] || 'text-pp-muted';
}

export function methodTintClass(method) {
  return METHOD_TINT[String(method || '').toUpperCase()] || 'bg-pp-fill text-pp-muted';
}

// 'pending' (no status yet) | 'error' (0 = network error, 4xx, 5xx) | 'redirect' (3xx) | 'success'
export function statusTone(code) {
  if (code === undefined || code === null) return 'pending';
  if (code === 0 || code >= 400) return 'error';
  if (code >= 300) return 'redirect';
  return 'success';
}

const TONE_TEXT = {
  pending: 'text-pp-faint',
  error: 'text-pp-delete',
  redirect: 'text-pp-put',
  success: 'text-pp-get',
};

const TONE_DOT = {
  pending: 'bg-pp-faint',
  error: 'bg-pp-delete',
  redirect: 'bg-pp-put',
  success: 'bg-pp-get',
};

const TONE_TINT = {
  pending: 'bg-pp-fill text-pp-muted',
  error: 'bg-pp-delete/[0.12] text-pp-delete',
  redirect: 'bg-pp-put/[0.14] text-pp-put',
  success: 'bg-pp-get/[0.14] text-pp-get',
};

export function statusTextClass(code) {
  return TONE_TEXT[statusTone(code)];
}

export function statusLabel(code) {
  if (code === undefined || code === null) return 'Pending';
  if (code === 0) return 'Failed';
  return String(code);
}

// Plain coloured method name, fixed width so paths line up in a list.
export function MethodLabel({ method, className = '' }) {
  const name = String(method || 'GET').toUpperCase();
  return (
    <span className={`font-mono text-2xs font-semibold tracking-wide ${methodTextClass(name)} ${className}`}>
      {name}
    </span>
  );
}

// Tinted pill, for headers and places where the method stands alone.
export function MethodBadge({ method, className = '' }) {
  const name = String(method || 'GET').toUpperCase();
  return (
    <span className={`pp-badge font-mono font-semibold tracking-wide ${methodTintClass(name)} ${className}`}>
      {name}
    </span>
  );
}

// Dot + code, for dense lists.
export function StatusDot({ code, className = '' }) {
  const tone = statusTone(code);
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-2xs tabular-nums ${tone === 'success' ? 'text-pp-muted' : TONE_TEXT[tone]} ${className}`}>
      <span className={`pp-dot !h-1.5 !w-1.5 ${TONE_DOT[tone]} ${tone === 'pending' ? 'animate-pulse' : ''}`} />
      {statusLabel(code)}
    </span>
  );
}

// Tinted pill with the code.
export function StatusBadge({ code, className = '' }) {
  return (
    <span className={`pp-badge font-mono font-semibold tabular-nums ${TONE_TINT[statusTone(code)]} ${className}`}>
      {statusLabel(code)}
    </span>
  );
}
